/* oxlint-disable sorare/enforce-monetary-reward-disclaimer -- Non-reward currency (wallet/market/JONUM limits/etc.) — disclaimer does not apply. */
import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';

import { HeadlineXS } from '@sorare/core/src/atoms/typography';
import {
  TransactionEntry,
  messages,
} from '@sorare/core/src/components/mySorare/TransactionsHistory/common/TransactionEntry';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { isType, isTypes } from '@sorare/core/src/lib/gql';

import { RequestReceipt } from '#src/components/mySorare/TransactionsHistory/SpentFiatPaymentIntents/RequestReceipt/index.tsx';

import type { AccountEntry_authorizationRequest } from './__generated__/index.graphql.ts';

export { messages };

type Props = {
  title: string | ReactNode;
  payment: AccountEntry_authorizationRequest;
  children: ReactNode;
};

export const AccountEntry = ({ title, payment, children }: Props) => {
  const { formatNumber } = useIntlContext();
  const { request, createdAt } = payment;
  if (
    !isTypes(request, [
      'MangopayCreditCardAuthorizationRequest',
      'StripeCreditCardAuthorizationRequest',
      'CheckoutCreditCardAuthorizationRequest',
    ]) ||
    !request.amount ||
    !request.currency
  )
    return null;

  return (
    <TransactionEntry
      date={createdAt}
      amount={
        <HeadlineXS as="h6" color="var(--c-white)">
          {formatNumber(-request.amount / 100, {
            style: 'currency',
            currency: request.currency,
          })}
        </HeadlineXS>
      }
      {...(isType(request, 'StripeCreditCardAuthorizationRequest')
        ? { action: <RequestReceipt receiptUrl={request.receiptUrl} /> }
        : {})}
      title={title}
    >
      {children}
    </TransactionEntry>
  );
};

AccountEntry.fragments = {
  payment: gql`
    fragment AccountEntry_authorizationRequest on AuthorizationRequest {
      id
      createdAt
      request {
        ... on MangopayCreditCardAuthorizationRequest {
          id
          amount
          currency
        }
        ... on StripeCreditCardAuthorizationRequest {
          id
          amount
          currency
          receiptUrl
        }
        ... on CheckoutCreditCardAuthorizationRequest {
          id
          amount
          currency
        }
      }
    }
  ` as TypedDocumentNode<AccountEntry_authorizationRequest>,
};
